/**
 * Classic reCAPTCHA v3: `api.js?render=SITE_KEY` + `grecaptcha.execute(...)`.
 *
 * The `react-google-recaptcha` package loads v2 (`render=explicit`) only — not for v3.
 *
 * @see https://developers.google.com/recaptcha/docs/v3
 */

declare global {
  interface Window {
    recaptchaOptions?: { useRecaptchaNet?: boolean };
    grecaptcha?: {
      ready: (callback: () => void) => void;
      execute: (
        siteKey: string,
        options: { action: string }
      ) => Promise<string>;
    };
  }
}

const loadPromises = new Map<string, Promise<void>>();

function recaptchaV3ScriptId(siteKey: string): string {
  return `recaptcha-v3-${siteKey.replace(/[^a-zA-Z0-9]/g, "")}`;
}

function recaptchaScriptHost(): string {
  if (typeof window === "undefined") {
    return "www.google.com";
  }
  return window.recaptchaOptions?.useRecaptchaNet === true
    ? "www.recaptcha.net"
    : "www.google.com";
}

function buildRecaptchaV3ScriptSrc(siteKey: string, hl?: string): string {
  const host = recaptchaScriptHost();
  let src = `https://${host}/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`;
  if (hl) {
    src += `&hl=${encodeURIComponent(hl)}`;
  }
  return src;
}

function loadRecaptchaV3Script(siteKey: string, hl?: string): Promise<void> {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("reCAPTCHA v3 requires a browser"));
  }

  const cacheKey = `${siteKey}:${hl ?? ""}`;
  const cached = loadPromises.get(cacheKey);
  if (cached) {
    return cached;
  }

  const promise = new Promise<void>((resolve, reject) => {
    const scriptId = recaptchaV3ScriptId(siteKey);
    const api = window.grecaptcha;
    if (document.getElementById(scriptId)) {
      if (!api?.ready) {
        reject(new Error("reCAPTCHA v3 API unavailable"));
        return;
      }
      api.ready(() => resolve());
      return;
    }

    const script = document.createElement("script");
    script.id = scriptId;
    script.src = buildRecaptchaV3ScriptSrc(siteKey, hl);
    script.async = true;
    script.defer = true;
    script.onload = () => {
      const loaded = window.grecaptcha;
      if (!loaded?.ready) {
        reject(new Error("reCAPTCHA v3 API unavailable after load"));
        return;
      }
      loaded.ready(() => resolve());
    };
    script.onerror = () => {
      loadPromises.delete(cacheKey);
      reject(new Error("reCAPTCHA v3 script failed to load"));
    };
    document.head.appendChild(script);
  });

  loadPromises.set(cacheKey, promise);
  return promise;
}

/** v3 action names: alphanumeric, slashes, underscores only (Google rules). */
export const RECAPTCHA_V3_ACTIONS = {
  USER_REGISTRATION: "user_registration",
  FORGOT_PASSWORD: "forgot_password",
  NEWSLETTER: "newsletter",
  BIKE_REGISTRATION_STEP2_MANUAL: "bike_registration_step2_manual",
} as const;

export type RecaptchaV3Action =
  (typeof RECAPTCHA_V3_ACTIONS)[keyof typeof RECAPTCHA_V3_ACTIONS];

/**
 * Returns a v3 token, or null if the script cannot load or execute fails.
 */
export async function getRecaptchaV3Token(
  siteKey: string,
  action: RecaptchaV3Action | string,
  options?: { hl?: string }
): Promise<string | null> {
  try {
    await loadRecaptchaV3Script(siteKey, options?.hl);
    const api = window.grecaptcha;
    if (!api?.ready) {
      return null;
    }
    return await new Promise<string>((resolve, reject) => {
      api.ready(() => {
        api.execute(siteKey, { action }).then(resolve).catch(reject);
      });
    });
  } catch {
    return null;
  }
}
